import { createFoundryAuth } from "./foundry-auth";
import { createMcpClient } from "./mcp-client";
import { createResponsesClient } from "./responses";
import { createSessionApi } from "./session-api";

import type { UnityApiTransport } from "./session-api";

export { buildPageClip } from "./page-clip";
export type { PageClip, PageClipOptions, PageImageUrlProvider } from "./page-clip";

/** The `agent` block of a site's config.json. */
export interface AgentConfig {
  agentEndpoint: string;
  mcpServerUrl: string;
  clientId: string;
  tenantId: string;
  authority?: string;
  /** Backend environment name (e.g. "Dev") for tenants with more than one — omit for single-environment tenants. */
  environmentName?: string;
}

export interface AgentClientOptions {
  agent: AgentConfig;
  /** The signed-in user's email, so Entra sign-in for the agent can skip the account picker. */
  getLoginHint: () => string | undefined;
  unityApi: UnityApiTransport;
}

/**
 * Everything a site needs to talk to the Unity Agent: the Foundry hosted agent (Responses API),
 * the MCP server its widgets call back into, and UnityApi's session endpoints. Create one per page.
 */
export function createAgentClient({ agent, getLoginHint, unityApi }: AgentClientOptions) {
  const responses = createResponsesClient(agent, createFoundryAuth(agent, getLoginHint));
  const mcp = createMcpClient(agent, responses.getConversationKey);
  return { ...responses, ...mcp, session: createSessionApi(unityApi) };
}

export type AgentClient = ReturnType<typeof createAgentClient>;

export { createChatSession, MAX_FILE_CHIPS } from "./chat-session";
export { createSessionApi } from "./session-api";
export { connectMcpApp, isMcpApp, readWidgetNavigation, withCspNonce } from "./mcp-app-host";

export type { ChatSession, ChatState } from "./chat-session";
export type { McpAppHandlers, WidgetNavigation } from "./mcp-app-host";
export type {
  AgentReply,
  ChatEvent,
  ChatTurn,
  CitationNavigation,
  FileChip,
  McpUiResource,
  RawCallToolResult,
  TokenUsage,
} from "./responses";
export type { UnityApiTransport } from "./session-api";
